import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { LuCheck, LuChevronDown, LuChevronUp, LuPause, LuPlay } from "react-icons/lu";
import { fetchAzureVoiceSamples, fetchElevenLabsVoices } from "./talkingAvatarApi";
import { getTalkingAvatarPrefs, setTalkingAvatarPrefs } from "./talkingAvatarJobStore";
import { tt } from "./talkingAvatarI18n";
import {
  attachVoiceSamples,
  azurePickerVoices,
  pickVoiceSampleUrl,
  sameVoice,
  voiceFromAzure,
  voiceFromPrivate,
  voiceFromStock,
  voiceProviderLabel,
  voiceProviderOf,
  VOICE_PROVIDER_AZURE,
  VOICE_PROVIDER_ELEVENLABS,
} from "./talkingAvatarVoice";
import "./_talkingAvatar.scss";

// ElevenLabs TTS model (audio-driven models only — speech-to-speech has a
// single model): v2_5 → turbo v2.5, v3 → eleven_v3. Same choice as AIVoiceTab.
const ELEVENLABS_MODEL_OPTIONS = [
  { model: "v2_5", label: "ElevenLabs 2.5" },
  { model: "v3", label: "ElevenLabs 3" },
];
const elevenLabsModelLabel = (model) => `ElevenLabs ${model === "v2_5" ? "2.5" : "3"}`;

// Voice picker for the talking-avatar panel — the same look and markup as the
// lip-sync tab's voice picker (selected-voice row + "Sesi değiştir", then
// Provider / Gender chips, a card grid and numbered pages), with only the
// providers that can convert a voice: ElevenLabs (stock + the org's cloned
// voices, one list) and Azure (its voice-conversion voices; for an
// audio-driven model every Azure TTS voice — azurePickerVoices). Every list is
// small, so it is loaded once and filtered/paged here, 12 per page like the
// lip-sync picker. A provider that cannot be used stays visible, disabled,
// with the reason as its tooltip; without the provider list (loading, failed,
// an older API) only ElevenLabs is offered.
// Controlled: `value` is a normalized voice (talkingAvatarVoice.js).
// audioDriven (the model animates the avatar to synthesized speech, e.g.
// OmniHuman): Azure offers all its TTS voices too, and the hint says the
// narration is voiced in them rather than converted to them.

const PAGE_SIZE = 12;
// One request for every stock ElevenLabs voice (the API has no page-size cap).
const STOCK_FETCH_SIZE = 200;
const ALL = "";

let stockCache = null; // Promise<voice[]> shared by every picker instance
const loadAllStockVoices = () => {
  if (!stockCache) {
    stockCache = fetchElevenLabsVoices({ page: 1, pageSize: STOCK_FETCH_SIZE })
      .then((res) => (res && Array.isArray(res.voices) ? res.voices : []).map(voiceFromStock))
      .catch((err) => {
        stockCache = null; // a failure is not cached
        throw err;
      });
  }
  return stockCache;
};
export const __resetTalkingVoicePickerCache = () => {
  stockCache = null;
};

const normalizeGender = (g) => {
  const v = String(g ?? "")
    .toLowerCase()
    .trim();
  if (v === "male" || v === "m" || v === "erkek") return "male";
  if (v === "female" || v === "f" || v === "kadın" || v === "kadin") return "female";
  if (v === "neutral" || v === "nötr" || v === "notr") return "neutral";
  return "";
};

// Same gender colours as the rest of the sound modal (male blue, female pink).
const genderStyle = (g) => {
  const n = normalizeGender(g);
  if (n === "male") return { background: "#e0f2fe", color: "#2563eb" };
  if (n === "female") return { background: "#ffe4f0", color: "#db2777" };
  return { background: "#ede9fe", color: "#7c3aed" };
};

const genderLabel = (g) => {
  const n = normalizeGender(g);
  if (n === "male") return tt("TalkingAvatar.Voice.Male", "Erkek");
  if (n === "female") return tt("TalkingAvatar.Voice.Female", "Kadın");
  if (n === "neutral") return tt("TalkingAvatar.Voice.Neutral", "Nötr");
  return "";
};

const voiceKey = (v) => `${voiceProviderOf(v)}:${v.isPrivate ? "p" : "s"}:${v.id}`;

// Why a provider cannot be picked, or null when it can.
const providerBlockReason = (p) => {
  if (!p) return null;
  if (!p.available) return tt("TalkingAvatar.Voice.ProviderUnconfigured", "yapılandırılmamış");
  if (!p.priced) return tt("TalkingAvatar.Voice.ProviderUnpriced", "fiyat tanımlı değil");
  return null;
};

export default function TalkingVoicePicker({
  value,
  onChange,
  orgVoices,
  orgLoading,
  orgError,
  open,
  onOpenChange,
  providers,
  audioDriven,
  elevenLabsModel,
  onElevenLabsModelChange,
}) {
  // ElevenLabs label: with the TTS model when the model is audio-driven.
  const providerLabel = (id) =>
    audioDriven && id === VOICE_PROVIDER_ELEVENLABS
      ? elevenLabsModelLabel(elevenLabsModel)
      : voiceProviderLabel(id);
  const [filter, setFilter] = useState(() =>
    value ? voiceProviderOf(value) : getTalkingAvatarPrefs().voiceFilter || ALL
  );
  const [gender, setGender] = useState(() => getTalkingAvatarPrefs().voiceGender || "");
  const [page, setPage] = useState(1);
  const [stock, setStock] = useState({ voices: [], loading: false, error: false });
  const [azureSamples, setAzureSamples] = useState(null);
  const [playingKey, setPlayingKey] = useState(null);
  const audioRef = useRef(null);

  // ── Providers ───────────────────────────────────────────────────────────
  // ElevenLabs missing from the list counts as usable (it always was); Azure
  // exists only once the API has listed it.
  const providerInfo = useMemo(() => {
    const list = Array.isArray(providers) ? providers : [];
    const azure = list.find((p) => p && p.id === VOICE_PROVIDER_AZURE) || null;
    const elevenlabs = list.find((p) => p && p.id === VOICE_PROVIDER_ELEVENLABS) || {
      id: VOICE_PROVIDER_ELEVENLABS,
      available: true,
      priced: true,
    };
    return { [VOICE_PROVIDER_ELEVENLABS]: elevenlabs, [VOICE_PROVIDER_AZURE]: azure };
  }, [providers]);
  const providerIds = providerInfo[VOICE_PROVIDER_AZURE]
    ? [VOICE_PROVIDER_ELEVENLABS, VOICE_PROVIDER_AZURE]
    : [VOICE_PROVIDER_ELEVENLABS];
  const blockReason = (id) => providerBlockReason(providerInfo[id]);
  const usable = (id) => providerIds.includes(id) && !blockReason(id);

  // The filter on screen: the chosen provider, or "Tümü" once it is unusable.
  const activeFilter = filter && !usable(filter) ? ALL : filter;
  const includes = (id) => usable(id) && (activeFilter === ALL || activeFilter === id);
  const showElevenLabs = includes(VOICE_PROVIDER_ELEVENLABS);
  const showAzure = includes(VOICE_PROVIDER_AZURE);

  // Opening the picker shows the selected voice's provider.
  const valueRef = useRef(value);
  valueRef.current = value;
  useEffect(() => {
    if (open && valueRef.current) setFilter(voiceProviderOf(valueRef.current));
  }, [open]);

  const stopSample = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingKey(null);
  }, []);

  useEffect(() => () => stopSample(), [stopSample]);
  useEffect(() => {
    if (!open) stopSample();
  }, [open, stopSample]);

  // ── Data ────────────────────────────────────────────────────────────────
  const loadStock = open && showElevenLabs;
  useEffect(() => {
    if (!loadStock) return undefined;
    let cancelled = false;
    setStock((s) => ({ ...s, loading: true, error: false }));
    loadAllStockVoices()
      .then((voices) => {
        if (!cancelled) setStock({ voices, loading: false, error: false });
      })
      .catch(() => {
        if (!cancelled) setStock({ voices: [], loading: false, error: true });
      });
    return () => {
      cancelled = true;
    };
  }, [loadStock]);

  // Azure samples: optional — a voice without one just has no play button.
  const needSamples = (open && showAzure) || voiceProviderOf(value) === VOICE_PROVIDER_AZURE;
  useEffect(() => {
    if (!needSamples || azureSamples) return undefined;
    let cancelled = false;
    fetchAzureVoiceSamples()
      .then((list) => {
        if (!cancelled) setAzureSamples(Array.isArray(list) ? list : []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [needSamples, azureSamples]);

  const azureVoices = useMemo(() => {
    const list = azurePickerVoices(providerInfo[VOICE_PROVIDER_AZURE], audioDriven)
      .map(voiceFromAzure)
      .filter(Boolean);
    return attachVoiceSamples(list, azureSamples);
  }, [providerInfo, azureSamples, audioDriven]);

  const orgList = useMemo(
    () => (Array.isArray(orgVoices) ? orgVoices : []).map(voiceFromPrivate).filter(Boolean),
    [orgVoices]
  );

  // ElevenLabs = the org's own voices first, then the stock ones.
  const listed = useMemo(() => {
    const all = [
      ...(showElevenLabs ? [...orgList, ...stock.voices] : []),
      ...(showAzure ? azureVoices : []),
    ];
    return gender ? all.filter((v) => normalizeGender(v.gender) === gender) : all;
  }, [showElevenLabs, showAzure, orgList, stock.voices, azureVoices, gender]);

  const totalPages = Math.max(1, Math.ceil(listed.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const shown = listed.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const loading = showElevenLabs && (stock.loading || orgLoading) && listed.length === 0;
  const failed = showElevenLabs && (stock.error || orgError) && listed.length === 0;

  // The selected voice with a sample (Azure samples arrive later).
  const valueWithSample = useMemo(() => {
    if (!value) return null;
    if (voiceProviderOf(value) !== VOICE_PROVIDER_AZURE) return value;
    return azureVoices.find((v) => sameVoice(v, value)) || value;
  }, [value, azureVoices]);

  // ── Actions ─────────────────────────────────────────────────────────────
  const playSample = (voice) => {
    const key = voiceKey(voice);
    if (playingKey === key) {
      stopSample();
      return;
    }
    stopSample();
    const url = pickVoiceSampleUrl(voice);
    if (!url) return;
    const audio = new Audio(url);
    audioRef.current = audio;
    setPlayingKey(key);
    audio.onended = () => {
      if (audioRef.current === audio) stopSample();
    };
    audio.play().catch(() => {
      if (audioRef.current === audio) stopSample();
    });
  };

  const select = (voice) => {
    stopSample();
    onChange?.(voice);
  };

  const switchFilter = (next) => {
    if (next && !usable(next)) return;
    stopSample();
    setFilter(next);
    setPage(1);
    setTalkingAvatarPrefs({ voiceFilter: next });
  };

  const switchGender = (next) => {
    setGender(next);
    setPage(1);
    setTalkingAvatarPrefs({ voiceGender: next });
  };

  // ── Render ──────────────────────────────────────────────────────────────
  const renderCard = (voice) => {
    const active = sameVoice(value, voice) && !!value && !!value.isPrivate === !!voice.isPrivate;
    const key = voiceKey(voice);
    const hasSample = !!pickVoiceSampleUrl(voice);
    return (
      <div key={key} className="add-sound-modal__voice-card-wrapper">
        <div
          role="button"
          tabIndex={0}
          className={`add-sound-modal__voice-card${active ? " add-sound-modal__voice-card--active" : ""}`}
          style={{ cursor: "pointer" }}
          onClick={() => select(voice)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              select(voice);
            }
          }}
        >
          <button
            type="button"
            className="add-sound-modal__voice-card-play"
            style={genderStyle(voice.gender)}
            disabled={!hasSample}
            title={
              playingKey === key
                ? tt("TalkingAvatar.Voice.Stop", "Durdur")
                : tt("TalkingAvatar.Voice.Play", "Örneği dinle")
            }
            onClick={(e) => {
              e.stopPropagation();
              playSample(voice);
            }}
          >
            {playingKey === key ? <LuPause /> : <LuPlay />}
          </button>
          <div className="add-sound-modal__voice-card-body">
            <div className="add-sound-modal__voice-card-name">{voice.name}</div>
            <div className="add-sound-modal__voice-card-meta">
              {genderLabel(voice.gender) && <span>{genderLabel(voice.gender)}</span>}
              <span>{voiceProviderOf(voice)}</span>
            </div>
          </div>
          {active && (
            <span className="add-sound-modal__voice-card-check">
              <LuCheck size={14} />
            </span>
          )}
        </div>
      </div>
    );
  };

  const chip = ({ id, label, on, disabled, title, provider, onClick }) => (
    <button
      key={id || "all"}
      type="button"
      disabled={disabled}
      title={title}
      aria-pressed={on}
      className={`add-sound-modal__voice-filter-chip${
        provider ? " add-sound-modal__voice-filter-chip--provider" : ""
      }${on ? " add-sound-modal__voice-filter-chip--on" : ""}`}
      onClick={onClick}
    >
      {label}
    </button>
  );

  const emptyRow = (text) => <div className="add-sound-modal__voice-empty">{text}</div>;

  return (
    <div className="asm-ta__voice">
      <div className="asm-ta__voice-head">
        <label className="add-sound-modal__narration-label" style={{ margin: 0 }}>
          {tt("TalkingAvatar.Voice.Title", "Ses")} <span>*</span>
        </label>
        <span className="asm-ta__voice-hint">
          {audioDriven
            ? tt(
                "TalkingAvatar.Voice.HintAudioDriven",
                "Anlatım bu sesle seslendirilir; avatar bu sese göre konuşur."
              )
            : tt("TalkingAvatar.Voice.Hint", "Videodaki konuşma seçilen sese dönüştürülür.")}
        </span>
      </div>

      <div className="add-sound-modal__voice-profile">
        <div className="add-sound-modal__voice-row">
          <div className="add-sound-modal__voice-avatar" style={genderStyle(value?.gender)}>
            {value?.name ? String(value.name).trim().charAt(0).toUpperCase() : "?"}
          </div>
          <div className="add-sound-modal__voice-details">
            <div className="add-sound-modal__voice-title-row">
              <span className="add-sound-modal__voice-name">
                {value?.name || tt("TalkingAvatar.Voice.Select", "Ses seçin")}
              </span>
              {value && (
                <span className="add-sound-modal__voice-pill">{providerLabel(value.provider)}</span>
              )}
              {value && genderLabel(value.gender) && (
                <span className="add-sound-modal__voice-pill">{genderLabel(value.gender)}</span>
              )}
            </div>
          </div>
          {valueWithSample && (
            <button
              type="button"
              className="add-sound-modal__voice-card-play"
              title={tt("TalkingAvatar.Voice.Play", "Örneği dinle")}
              aria-label={tt("TalkingAvatar.Voice.Play", "Örneği dinle")}
              disabled={!pickVoiceSampleUrl(valueWithSample)}
              onClick={() => playSample(valueWithSample)}
            >
              {playingKey === voiceKey(valueWithSample) ? <LuPause /> : <LuPlay />}
            </button>
          )}
          <button
            type="button"
            className="add-sound-modal__voice-action"
            onClick={() => onOpenChange?.(!open)}
          >
            {value
              ? tt("TalkingAvatar.Voice.ChangeVoice", "Sesi değiştir")
              : tt("TalkingAvatar.Voice.Select", "Ses seçin")}
            <span className="add-sound-modal__voice-action-icon">
              {open ? <LuChevronUp /> : <LuChevronDown />}
            </span>
          </button>
        </div>

        {open && (
          <div>
            <div className="add-sound-modal__voice-filters">
              <div className="add-sound-modal__voice-filter">
                <div className="add-sound-modal__voice-filter-label">
                  {tt("TalkingAvatar.Voice.ProviderFilter", "Sağlayıcı")}
                </div>
                <div className="add-sound-modal__voice-filter-chips">
                  {chip({
                    id: ALL,
                    label: tt("TalkingAvatar.Voice.All", "Tümü"),
                    on: activeFilter === ALL,
                    provider: true,
                    onClick: () => switchFilter(ALL),
                  })}
                  {providerIds.flatMap((id) => {
                    const reason = blockReason(id);
                    // Audio-driven + ElevenLabs: two chips, the TTS model is picked here.
                    if (audioDriven && id === VOICE_PROVIDER_ELEVENLABS) {
                      return ELEVENLABS_MODEL_OPTIONS.map((o) =>
                        chip({
                          id: `${id}-${o.model}`,
                          label: o.label,
                          on: activeFilter === id && elevenLabsModel === o.model,
                          disabled: !!reason,
                          title: reason || undefined,
                          provider: true,
                          onClick: () => {
                            if (activeFilter !== id) switchFilter(id);
                            onElevenLabsModelChange?.(o.model);
                          },
                        })
                      );
                    }
                    return [
                      chip({
                        id,
                        label: voiceProviderLabel(id),
                        on: activeFilter === id,
                        disabled: !!reason,
                        title: reason || undefined,
                        provider: true,
                        onClick: () => switchFilter(id),
                      }),
                    ];
                  })}
                </div>
              </div>

              <div className="add-sound-modal__voice-filter add-sound-modal__voice-filter--gender">
                <div className="add-sound-modal__voice-filter-label">
                  {tt("TalkingAvatar.Voice.GenderFilter", "Cinsiyet")}
                </div>
                <div className="add-sound-modal__voice-filter-chips">
                  {[
                    ["", tt("TalkingAvatar.Voice.All", "Tümü")],
                    ["female", tt("TalkingAvatar.Voice.Female", "Kadın")],
                    ["male", tt("TalkingAvatar.Voice.Male", "Erkek")],
                  ].map(([id, label]) =>
                    chip({ id, label, on: gender === id, onClick: () => switchGender(id) })
                  )}
                </div>
              </div>
            </div>

            <div className="add-sound-modal__voice-list">
              {loading ? (
                emptyRow(tt("TalkingAvatar.Voice.Loading", "Sesler yükleniyor…"))
              ) : failed ? (
                emptyRow(tt("TalkingAvatar.Voice.LoadError", "Sesler yüklenemedi."))
              ) : shown.length === 0 ? (
                emptyRow(tt("TalkingAvatar.Voice.Empty", "Ses bulunamadı."))
              ) : (
                <>
                  {shown.map(renderCard)}
                  <div className="add-sound-modal__voice-list-footer">
                    <span className="add-sound-modal__voice-count">
                      {tt("TalkingAvatar.Voice.Count", "{{shown}} / {{total}} Sesler", {
                        shown: shown.length,
                        total: listed.length,
                      })}
                      {activeFilter && ` · ${providerLabel(activeFilter)}`}
                    </span>
                    {totalPages > 1 && (
                      <div className="add-sound-modal__voice-pagination">
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                          <button
                            key={n}
                            type="button"
                            className={`add-sound-modal__voice-pagination-btn${
                              currentPage === n
                                ? " add-sound-modal__voice-pagination-btn--active"
                                : ""
                            }`}
                            onClick={() => setPage(n)}
                          >
                            {n}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

TalkingVoicePicker.propTypes = {
  value: PropTypes.object,
  onChange: PropTypes.func,
  orgVoices: PropTypes.array,
  orgLoading: PropTypes.bool,
  orgError: PropTypes.bool,
  open: PropTypes.bool,
  onOpenChange: PropTypes.func,
  // api/talking-avatar/voice-providers (normalized); null while unknown.
  providers: PropTypes.array,
  // The chosen model is audio-driven: the hint reads "voiced in", not "converted to".
  audioDriven: PropTypes.bool,
  // ElevenLabs TTS model ('v2_5' | 'v3') — shown/picked only when audioDriven.
  elevenLabsModel: PropTypes.oneOf(["v2_5", "v3"]),
  onElevenLabsModelChange: PropTypes.func,
};
